import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FileEditorPanel from './FileEditorPanel';

/**
 * Modal for viewing and editing workspace details
 * Uses workspace data passed as prop (from list) - no additional API call needed
 */
function WorkspaceDetailsModal({ workspace, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'files', label: 'Files' },
        { id: 'config', label: 'Configuration' }
    ];

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                />

                {/* Modal */}
                <div className="relative inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle bg-white rounded-lg shadow-xl transform transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">
                                {workspace.bot_id}
                            </h3>
                            <p className="text-sm text-gray-500 truncate max-w-md" title={workspace.path}>
                                {workspace.path}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 px-6">
                        <nav className="flex space-x-8">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        py-4 px-1 border-b-2 font-medium text-sm
                                        ${activeTab === tab.id
                                            ? 'border-indigo-500 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                        {activeTab === 'overview' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Bot ID</h4>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {workspace.bot_id}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">File Count</h4>
                                        <p className="mt-1 text-sm text-gray-900">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {workspace.file_count || 0} files
                                            </span>
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Size</h4>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {formatBytes(workspace.size_bytes)}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Last Modified</h4>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {formatDate(workspace.modified_at)}
                                        </p>
                                    </div>
                                </div>

                                {/* Workspace Path */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Path</h4>
                                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                                        {workspace.path}
                                    </p>
                                </div>

                                {/* Files List */}
                                {workspace.files && workspace.files.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">Knowledge Files</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {workspace.files.map((file, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                                                >
                                                    {file}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'files' && (
                            <FileEditorPanel workspaceId={workspace.bot_id} />
                        )}

                        {activeTab === 'config' && (
                            <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-700">Workspace Data</h4>
                                <pre className="p-4 bg-gray-50 rounded-md text-sm text-gray-800 overflow-x-auto">
                                    {JSON.stringify(workspace, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

WorkspaceDetailsModal.propTypes = {
    workspace: PropTypes.shape({
        bot_id: PropTypes.string.isRequired,
        path: PropTypes.string,
        modified_at: PropTypes.string,
        size_bytes: PropTypes.number,
        file_count: PropTypes.number,
        files: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

export default WorkspaceDetailsModal;
